/**
 * ClaimLegacy — self-serve migration for pilots who already pay through the
 * old privatejetpilots.com Stripe. They enter their legacy email + last-4
 * of the card that's on file; if we find a matching active subscription we
 * let them set a new PJP password and log them straight in with Pro
 * access pre-attached.
 *
 * Two-step form: [verify] → [finish].
 */
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle, Field } from "@/components/ui-hud";
import { Loader2, ShieldCheck, ArrowRight, KeyRound, CreditCard, CheckCircle2 } from "lucide-react";

export default function ClaimLegacy() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState(1); // 1 = verify, 2 = finish

  // Step 1 form
  const [verifyForm, setVerifyForm] = useState({ email: "", last4: "" });
  const [preview, setPreview] = useState(null);
  const [claimToken, setClaimToken] = useState(null);
  const [busy, setBusy] = useState(false);

  // Step 2 form
  const [finishForm, setFinishForm] = useState({
    password: "",
    first_name: "",
    last_name: "",
    home_base_city: "",
    role: "pilot",
  });

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/claim/verify", {
        email: verifyForm.email.trim().toLowerCase(),
        last4: verifyForm.last4.trim(),
      });
      setClaimToken(r.data.claim_token);
      setPreview(r.data.preview);
      setFinishForm((f) => ({ ...f, first_name: r.data.preview.first_name || "" }));
      setStep(2);
      toast.success("Subscription found — set your PJP password.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not verify — double-check the email and card last-4.");
    } finally { setBusy(false); }
  };

  const finish = async (e) => {
    e.preventDefault();
    if (finishForm.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/claim/finish", {
        claim_token: claimToken,
        password: finishForm.password,
        first_name: finishForm.first_name.trim(),
        last_name: finishForm.last_name.trim(),
        home_base_city: finishForm.home_base_city.trim() || null,
        role: finishForm.role,
      });
      localStorage.setItem("pjp_token", r.data.token);
      setUser(r.data.user);
      toast.success(`Welcome back, ${r.data.user.first_name}. Your subscription is live.`);
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <SectionTitle
        overline="// LEGACY MEMBER"
        title="Claim your PJP account"
        subtitle="Already paying on the old privatejetpilots.com? Verify your subscription and we'll bring you across with Pro access — no double charge."
      />

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6" data-testid="claim-stepper">
        <StepDot n={1} label="Verify" active={step >= 1} done={step > 1} />
        <div className="h-px flex-1 bg-white/10" />
        <StepDot n={2} label="Set password" active={step >= 2} done={false} />
      </div>

      {step === 1 && (
        <Card testId="claim-step-verify">
          <form onSubmit={verify} className="space-y-4">
            <Field label="Legacy email on your old subscription">
              <div className="relative">
                <input
                  type="email"
                  required
                  autoFocus
                  data-testid="claim-input-email"
                  value={verifyForm.email}
                  onChange={(e) => setVerifyForm({ ...verifyForm, email: e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan/50"
                  placeholder="you@example.com"
                />
              </div>
            </Field>
            <Field label="Last 4 digits of the card on file">
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{4}"
                  maxLength={4}
                  required
                  data-testid="claim-input-last4"
                  value={verifyForm.last4}
                  onChange={(e) => setVerifyForm({ ...verifyForm, last4: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white font-mono tracking-[0.35em] focus:outline-none focus:border-cyan/50"
                  placeholder="4242"
                />
                <CreditCard className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/35" />
              </div>
              <div className="text-[11px] text-white/45 mt-1">
                We check this against your Stripe records — the card is not stored anywhere.
              </div>
            </Field>
            <button
              type="submit"
              disabled={busy || verifyForm.last4.length !== 4}
              data-testid="claim-verify-submit"
              className="btn-hud btn-hud-solid w-full justify-center"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> Verify subscription</>}
            </button>
            <div className="text-[12px] text-white/55 pt-1 border-t border-white/[0.06] mt-2">
              Don't have a legacy subscription?{" "}
              <Link to="/register" className="text-cyan hover:text-white">Create a new PJP account →</Link>
            </div>
          </form>
        </Card>
      )}

      {step === 2 && (
        <Card testId="claim-step-finish">
          <div
            className="rounded-lg border border-avail/40 bg-avail/[0.08] p-3 mb-4 flex items-center gap-2"
            data-testid="claim-preview-banner"
          >
            <CheckCircle2 className="h-4 w-4 text-avail flex-shrink-0" />
            <div className="text-[13px] text-white/85">
              <span className="text-avail font-semibold">Match found</span>
              {preview?.price?.amount ? (
                <> — Active subscription <span className="text-white">${(preview.price.amount / 100).toLocaleString()}/{preview.price.interval || "yr"}</span></>
              ) : null}
              {preview?.current_period_end ? (
                <> · next renewal <span className="text-white">{new Date(preview.current_period_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span></>
              ) : null}
            </div>
          </div>
          <form onSubmit={finish} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name">
                <input
                  required
                  data-testid="claim-input-first"
                  value={finishForm.first_name}
                  onChange={(e) => setFinishForm({ ...finishForm, first_name: e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan/50"
                />
              </Field>
              <Field label="Last name">
                <input
                  required
                  data-testid="claim-input-last"
                  value={finishForm.last_name}
                  onChange={(e) => setFinishForm({ ...finishForm, last_name: e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan/50"
                />
              </Field>
            </div>
            <Field label="Home base — city or ICAO (optional)">
              <input
                data-testid="claim-input-home"
                value={finishForm.home_base_city}
                onChange={(e) => setFinishForm({ ...finishForm, home_base_city: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan/50"
                placeholder="KTEB or Miami"
              />
            </Field>
            <Field label="Choose a PJP password (min 8 chars)">
              <div className="relative">
                <input
                  type="password"
                  required
                  minLength={8}
                  data-testid="claim-input-password"
                  value={finishForm.password}
                  onChange={(e) => setFinishForm({ ...finishForm, password: e.target.value })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 pr-9 text-sm text-white focus:outline-none focus:border-cyan/50"
                  placeholder="••••••••"
                />
                <KeyRound className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/35" />
              </div>
            </Field>
            <button
              type="submit"
              disabled={busy}
              data-testid="claim-finish-submit"
              className="btn-hud btn-hud-solid w-full justify-center"
              style={{ boxShadow: "0 0 20px rgba(46,213,115,0.45)" }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Claim my PJP account <ArrowRight className="h-4 w-4" /></>}
            </button>
            <button
              type="button"
              onClick={() => { setStep(1); setClaimToken(null); setPreview(null); }}
              data-testid="claim-back"
              className="w-full text-[11.5px] text-white/45 hover:text-white/80"
            >
              ← Wrong email or card — start over
            </button>
          </form>
        </Card>
      )}
    </div>
  );
}

function StepDot({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`h-7 w-7 rounded-full grid place-items-center mono text-[11px] font-bold border ${
          done ? "bg-avail text-obsidian border-avail"
            : active ? "text-cyan border-cyan bg-cyan/10"
            : "text-white/40 border-white/15"
        }`}
      >
        {done ? "✓" : n}
      </div>
      <div className={`mono text-[10px] tracking-widest uppercase ${active || done ? "text-white/85" : "text-white/40"}`}>
        {label}
      </div>
    </div>
  );
}
